import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { UserDto } from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import { useLocationId } from "../../../stores/User/User.hooks";
import { useUsersByLocation } from "../../../stores/Calendar/calendar.queries";
import { useAppointmentsByRx } from "../../../stores/Appointment/appointment.queries";
import notificationStore from "../../../stores/Notification/notificationStore";

interface StartDoctorReportDialogProps {
  open: boolean;
  onClose: () => void;
  rxId: string;
  onCreated?: (reportId: string) => void;
}

/**
 * Modal dialog to start (create) a doctor report for a given Rx.
 * Checks if a report already exists for the Rx and prevents duplicates.
 * Allows the user to select which therapist to assign the report to.
 */
const StartDoctorReportDialog: React.FC<StartDoctorReportDialogProps> = ({
  open,
  onClose,
  rxId,
  onCreated,
}) => {
  const { clinicalDocumentation } = useApi();
  const locationId = useLocationId();
  const { data: locationUsers = [], isLoading: usersLoading } = useUsersByLocation(
    locationId,
    undefined,
    true,
    !!locationId && open
  );

  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [existingReportError, setExistingReportError] = useState(false);

  // Fetch appointments for this Rx to find the latest therapist
  const { data: rxAppointments = [] } = useAppointmentsByRx(rxId);

  // Pre-select the therapist from the latest appointment
  useEffect(() => {
    if (selectedUser || !locationUsers.length || !rxAppointments.length) return;

    // Sort by start date descending (latest first)
    const sortedAppointments = [...rxAppointments]
      .filter((apt) => apt.timeSlots?.length)
      .sort((a, b) => (b.start || "").localeCompare(a.start || ""));

    // Find the first appointment with a therapist user
    for (const apt of sortedAppointments) {
      const therapistUser = apt.timeSlots?.find((ts) => ts.user?.id)?.user;
      if (therapistUser?.id) {
        const matchedUser = locationUsers.find((u) => u.id === therapistUser.id);
        if (matchedUser) {
          setSelectedUser(matchedUser);
          break;
        }
      }
    }
  }, [rxAppointments, locationUsers, selectedUser]);

  const handleCreate = async () => {
    if (!rxId) return;
    setLoading(true);
    setExistingReportError(false);

    try {
      // Check if a doctor report already exists for this Rx
      const existing = await clinicalDocumentation.getClinicalDoctorReportByRxId(rxId);
      if (existing && existing.id) {
        setExistingReportError(true);
        setLoading(false);
        return;
      }
    } catch {
      // 404 or error means no existing report - proceed
    }

    try {
      const newReport = await clinicalDocumentation.postClinicalDoctorReport({
        rxId,
        assignedToUserId: selectedUser?.id,
      });
      notificationStore.showMessage("Therapiebericht erfolgreich erstellt", "success");
      onCreated?.((newReport as any).id);
      onClose();
    } catch (error: any) {
      notificationStore.showMessage(
        error?.message || "Therapiebericht konnte nicht erstellt werden",
        "error",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Therapiebericht starten</DialogTitle>
      <DialogContent>
        {existingReportError && (
          <Typography color="error" sx={{ mb: 2 }}>
            Für dieses Rezept existiert bereits ein Therapiebericht. Ein zweiter kann nicht erstellt werden.
          </Typography>
        )}
        <Typography variant="body2" sx={{ mb: 2 }}>
          Wählen Sie den Therapeuten, dem der Therapiebericht zugewiesen werden soll:
        </Typography>
        <Autocomplete
          options={locationUsers}
          loading={usersLoading}
          getOptionLabel={(user: UserDto) =>
            `${user.firstName || ""} ${user.lastName || ""}`.trim()
          }
          value={selectedUser}
          onChange={(_e, newValue) => setSelectedUser(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Therapeut"
              placeholder="Therapeut auswählen..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={loading || !selectedUser}>
          {loading ? <CircularProgress size={20} /> : "Erstellen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartDoctorReportDialog;
